import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import FormInput from '../components/ui/FormInput';

export default function LoginPage() {
  const { user, signInWithGoogle, signInWithEmail, resetPassword, loading, error, clearError } = useUser();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') ?? '';
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (showResetPassword) {
      if (resetLoading) return;
      try {
        setResetLoading(true);
        await resetPassword(email);
        setResetSent(true);
      } catch {
        // Error is handled in context
      } finally {
        setResetLoading(false);
      }
    } else {
      await signInWithEmail(email, password);
    }
  };

  const handleGoogleSignIn = async () => {
    await signInWithGoogle();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-enter">
        {/* Logo */}
        <div className="text-center mb-8">
          {theme.logo ? (
            <img src={theme.logo} alt={theme.name} className="h-12 mx-auto mb-2" />
          ) : (
            <h1 className="text-4xl font-bold tracking-tight">
              <span className="text-primary">{theme.name.toLowerCase()}</span>
            </h1>
          )}
          <p className="text-muted-foreground mt-2">{t('login.demo_subtitle')}</p>
        </div>

        {/* Login Card */}
        <div className="glass-card p-8">
          <h2 className="text-xl font-semibold text-foreground mb-6 text-center">
            {showResetPassword ? t('login.reset_title') : t('login.title')}
          </h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
              <button onClick={clearError} className="ml-auto text-destructive hover:text-destructive/80">&times;</button>
            </div>
          )}

          {/* Reset Password Success */}
          {resetSent && (
            <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg text-success text-sm">
              {t('login.reset_sent', { email })}
            </div>
          )}

          {/* Email/Password Form */}
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <FormInput
              icon={Mail}
              label={t('login.email')}
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              required
            />

            {!showResetPassword && (
              <FormInput
                icon={Lock}
                label={t('login.password')}
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.password_placeholder')}
                required={!showResetPassword}
              />
            )}

            <button
              type="submit"
              disabled={loading || resetLoading}
              className="btn-primary w-full py-3 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-primary-foreground font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {(loading || resetLoading) ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('login.loading')}
                </>
              ) : showResetPassword ? (
                t('login.reset_submit')
              ) : (
                t('login.submit')
              )}
            </button>
          </form>

          {/* Forgot Password / Back Link */}
          <div className="mt-4 text-center">
            {showResetPassword ? (
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setResetSent(false);
                  clearError();
                }}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('login.back_to_login')}
              </button>
            ) : (
              <a
                href="/wachtwoord-vergeten"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('login.forgot_password')}
              </a>
            )}
          </div>

          {/* Divider */}
          {!showResetPassword && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card text-muted-foreground">{t('login.or')}</span>
                </div>
              </div>

              {/* Google Sign In */}
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full py-3 bg-white hover:bg-gray-100 disabled:bg-gray-200 text-gray-900 font-semibold rounded-xl transition-colors flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('login.google')}
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 space-y-2">
          <p className="text-muted-foreground text-sm">
            {t('login.no_account')}{' '}
            <button
              onClick={() => navigate('/request-access')}
              className="text-primary hover:text-primary/80 transition-colors font-medium"
            >
              {t('login.request_access')}
            </button>
          </p>
          <button
            onClick={() => navigate('/')}
            className="text-muted-foreground/60 hover:text-muted-foreground text-sm transition-colors"
          >
            {t('login.back_home')}
          </button>
        </div>
      </div>
    </div>
  );
}
